import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useRef,
  useState, type ReactNode,
} from "react";
import type { Columna } from "../components/DataTable";

/** Lo que sabe escribir el CSV: dato CRUDO, nunca JSX. */
export type ValorCsv = string | number | null | undefined;

export interface BloqueExport {
  /** Título de la tarjeta; se escribe como línea sola antes de la tabla. */
  titulo?: string;
  /** Encabezado + filas. Es función porque se resuelve al hacer clic, no en cada render. */
  filas: () => ValorCsv[][];
  /** 0 = tarjetas de indicadores (van primero), 1 = tablas. Igual que el orden del original. */
  prioridad?: number;
}

interface Registro {
  bloque: BloqueExport;
  /** Orden de aparición; empata el orden visual de la vista. */
  secuencia: number;
}

interface AccionesExport {
  registrar: (id: string, bloque: BloqueExport) => void;
  quitar: (id: string) => void;
  recolectar: () => BloqueExport[];
}

// Dos contextos a propósito: las ACCIONES nunca cambian de identidad, así el
// efecto de limpieza de cada bloque no se vuelve a correr cuando otro bloque se
// registra (si no, quitar→registrar en cascada y el contador se cicla solo).
// El booleano vive aparte porque sí cambia, y solo el botón lo escucha.
const CtxAcciones = createContext<AccionesExport | null>(null);
const CtxHayAlgo = createContext(false);

/**
 * Registro de lo exportable de la vista activa.
 *
 * El Radar actual arma el CSV RASPANDO EL DOM (`sec.querySelectorAll("table.data")`
 * + `celdaExcel()` para des-formatear los números ya pintados). Aquí no hace falta:
 * cada vista registra el dato crudo y el botón junta lo registrado. Como las vistas
 * se desmontan al cambiar de pantalla (el `key={vista}` del Shell), el registro se
 * limpia solo: lo que ves es lo que se exporta.
 */
export function ExportarProvider({ children }: { children: ReactNode }) {
  const reg = useRef(new Map<string, Registro>());
  const seq = useRef(0);
  const [cuantos, setCuantos] = useState(0);

  const registrar = useCallback((id: string, bloque: BloqueExport) => {
    const previo = reg.current.get(id);
    // idempotente por id: re-registrar solo refresca el dato, no reordena
    reg.current.set(id, { bloque, secuencia: previo?.secuencia ?? seq.current++ });
    if (!previo) setCuantos((n) => n + 1);
  }, []);

  const quitar = useCallback((id: string) => {
    if (reg.current.delete(id)) setCuantos((n) => n - 1);
  }, []);

  const recolectar = useCallback(
    () =>
      [...reg.current.values()]
        .sort((a, b) => (a.bloque.prioridad ?? 1) - (b.bloque.prioridad ?? 1) || a.secuencia - b.secuencia)
        .map((r) => r.bloque),
    [],
  );

  const acciones = useMemo<AccionesExport>(
    () => ({ registrar, quitar, recolectar }),
    [registrar, quitar, recolectar],
  );

  return (
    <CtxAcciones.Provider value={acciones}>
      <CtxHayAlgo.Provider value={cuantos > 0}>{children}</CtxHayAlgo.Provider>
    </CtxAcciones.Provider>
  );
}

/** Acciones del registro; `null` fuera del Shell (login, pruebas). */
export function useAccionesExport(): AccionesExport | null {
  return useContext(CtxAcciones);
}

/** ¿La vista activa registró algo? Con `false` el botón se deshabilita. */
export function useHayQueExportar(): boolean {
  return useContext(CtxHayAlgo);
}

/**
 * Registra un bloque exportable mientras el componente esté montado.
 * Acepta `null` para NO registrar nada (una tabla vacía no debe exportarse),
 * así el hook siempre se llama y no rompe las reglas de hooks.
 */
export function useExportable(bloque: BloqueExport | null): void {
  const acc = useAccionesExport();
  const id = useId();

  // sin deps a propósito: en cada render se re-registra para que `filas()`
  // cierre sobre los datos vigentes (el registro es idempotente por id)
  useEffect(() => {
    if (!acc) return; // fuera del Shell (login, pruebas): no hay nada que exportar
    if (bloque) acc.registrar(id, bloque);
    else acc.quitar(id);
  });

  useEffect(() => () => acc?.quitar(id), [acc, id]);
}

export interface TileExport {
  indicador: string;
  valor: ValorCsv;
  detalle?: ValorCsv;
}

/**
 * Registra las tarjetas de indicadores de la vista (Indicador/Valor/Detalle),
 * el mismo primer bloque que exporta el Radar actual.
 * Es COMPONENTE y no hook para que se pueda poner en el JSX de vistas que
 * hacen `return` temprano mientras cargan (ahí un hook rompería el orden).
 */
export function ExportarTiles({ filas }: { filas: TileExport[] }): null {
  useExportable(
    filas.length
      ? {
          prioridad: 0,
          filas: () => [
            ["Indicador", "Valor", "Detalle"],
            ...filas.map((t) => [t.indicador, t.valor, t.detalle ?? ""]),
          ],
        }
      : null,
  );
  return null;
}

/**
 * Registra una tabla que NO se pinta con <DataTable> (heatmaps, tablas a mano).
 * Para las que sí, basta la prop `exportar` de <DataTable>.
 */
export function Exportable<T>({
  titulo, columnas, filas,
}: {
  titulo?: string;
  columnas: Columna<T>[];
  filas: T[];
}): null {
  useExportable(filas.length ? { titulo, filas: () => matriz(columnas, filas) } : null);
  return null;
}

/** Columnas + filas → encabezado y celdas crudas. */
export function matriz<T>(columnas: Columna<T>[], filas: T[]): ValorCsv[][] {
  return [
    columnas.map((c) => c.titulo),
    ...filas.map((f) => columnas.map((c) => c.valor(f))),
  ];
}

/**
 * Una celda → valor que Excel entienda. Portado de celdaExcel() (app.js 1348):
 * "$12,345.67" y "1,234" se vuelven números de verdad, "—" queda vacío y el
 * resto se queda tal cual. Aquí casi todo llega ya crudo, pero las columnas
 * que devuelven texto formateado siguen saliendo bien.
 */
export function celdaExcel(v: ValorCsv): string {
  if (v == null) return "";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  const s = v.trim();
  if (s === "—" || s === "–") return "";
  const limpio = s.replace(/^\$\s?/, "").replace(/,/g, "");
  if (
    limpio !== "" && !isNaN(Number(limpio)) && /\d/.test(limpio) &&
    // sin esta guarda "$  800" (doble espacio) saldría como " 800": Number() se
    // traga los espacios pero el texto que se escribe sigue trayendo el hueco
    limpio === limpio.trim() &&
    (s.startsWith("$") || /^[\d.,-]+$/.test(s))
  ) {
    return limpio;
  }
  return s;
}

/** Bloques → una sola matriz: línea en blanco entre bloques y título arriba de cada tabla. */
export function armarFilas(bloques: BloqueExport[]): ValorCsv[][] {
  const out: ValorCsv[][] = [];
  for (const b of bloques) {
    const filas = b.filas();
    if (!filas.length) continue;
    if (out.length) out.push([]);
    if (b.titulo) out.push([b.titulo]);
    out.push(...filas);
  }
  return out;
}

/**
 * Escribe el CSV con el MISMO formato del Radar actual (app.js 1335): separador
 * coma, comillas solo cuando hacen falta, y BOM al principio — sin el BOM, Excel
 * en Windows abre los acentos como basura.
 */
export function descargarCSV(nombre: string, filas: ValorCsv[][]): void {
  const csv = filas
    .map((r) =>
      r
        .map((c) => {
          const s = celdaExcel(c);
          return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
        })
        .join(","),
    )
    .join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = nombre.endsWith(".csv") ? nombre : nombre + ".csv";
  a.click();
  // el revoke inmediato cancela la descarga en Safari/Firefox: se espera
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}
